import {
  OrderBumpOfferItem,
  useOrderBumpsContextSafe,
} from '@/components/config-editor/providers/order-bumps-provider';
import { useProductsContextSafe } from '@/components/config-editor/providers/products-provider';
import { useUpsellsContextSafe } from '@/components/config-editor/providers/upsells-provider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import { ChevronDown, ImageOff, Package, Search, X } from 'lucide-react';
import { memo, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface OrderBumpSelectorProps {
  value: string;
  onChange: (value: string) => void;
  storeId?: string; // Store ID for filtering (optional, mainly for future server-side search)
  // Dependency injection - optional order bumps data
  orderBumps?: OrderBumpOfferItem[];
  loading?: boolean;
}

/**
 * Memoized order bump card component
 */
const OrderBumpCard = memo(
  ({
    orderBump,
    isSelected,
    onSelect,
    getPriceInfo,
    variantMap,
    className,
  }: {
    orderBump: OrderBumpOfferItem;
    isSelected: boolean;
    onSelect: () => void;
    getPriceInfo: (
      ob: OrderBumpOfferItem,
    ) => { amount: number; currency: string; recurring?: boolean; interval?: string | null } | null;
    variantMap: Map<string, { id: string; name: string; imageUrl: string | null }>;
    className?: string;
  }) => {
    const priceInfo = getPriceInfo(orderBump);
    // Get variant info from variantMap using variantId
    const variant = orderBump.variantId ? variantMap.get(orderBump.variantId) : null;
    const imageUrl = orderBump.overrideImageUrl || variant?.imageUrl || null;
    const displayName = variant?.name || 'Order Bump';
    const typeLabel =
      orderBump.type === 'primary'
        ? 'Primary'
        : orderBump.type === 'secondary'
          ? 'Secondary'
          : orderBump.type === 'vip'
            ? 'VIP'
            : 'Non-Interactive';

    return (
      <label
        className={cn(
          'flex cursor-pointer items-start gap-2 rounded border p-2 transition-all',
          'hover:border-primary/50 hover:bg-accent/50',
          'w-full',
          isSelected && 'border-primary bg-accent',
        )}
      >
        <RadioGroupItem value={orderBump.id} className="mt-0.5 shrink-0" />

        {imageUrl ? (
          <img src={imageUrl} alt={displayName} className="h-12 w-12 shrink-0 rounded object-cover" />
        ) : (
          <div className="bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded">
            <ImageOff className="text-muted-foreground h-6 w-6" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <h3 className="text-sm font-medium leading-tight">{displayName}</h3>
            <span className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">{typeLabel}</span>
          </div>

          {orderBump.displayPrice && priceInfo && (
            <p className="text-primary text-xs font-medium">
              {formatMoney(priceInfo.amount, priceInfo.currency)}
              {priceInfo.recurring && priceInfo.interval && (
                <span className="text-muted-foreground ml-1">/ {priceInfo.interval}</span>
              )}
            </p>
          )}
        </div>
      </label>
    );
  },
);

OrderBumpCard.displayName = 'OrderBumpCard';

function OrderBumpSelector({
  value,
  onChange,
  storeId,
  orderBumps: injectedOrderBumps,
  loading: injectedLoading,
}: OrderBumpSelectorProps) {
  const instanceId = useId();
  const { t } = useTranslation('storefront');
  // State
  const [openUpsells, setOpenUpsells] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Try to get order bumps from order-bumps-provider first (includes variantMap)
  const orderBumpsContext = useOrderBumpsContextSafe();
  // Fallback to extracting from upsells if order-bumps-provider is not available
  const upsellsContext = useUpsellsContextSafe();
  // Get products for variant information if order-bumps-provider is not available
  const productsContext = useProductsContextSafe();

  // Create variant map from products if not available from order-bumps-provider
  const variantMap = useMemo(() => {
    if (orderBumpsContext?.variantMap) {
      return orderBumpsContext.variantMap;
    }

    const map = new Map<string, { id: string; name: string; imageUrl: string | null }>();
    if (productsContext?.products) {
      productsContext.products.forEach((product) => {
        if (product.variants) {
          product.variants.forEach((variant) => {
            map.set(variant.id, {
              id: variant.id,
              name: variant.name,
              imageUrl: variant.imageUrl,
            });
          });
        }
      });
    }
    return map;
  }, [orderBumpsContext?.variantMap, productsContext?.products]);

  // Create price map from products if not available from order-bumps-provider
  const priceMap = useMemo(() => {
    if (orderBumpsContext?.priceMap) {
      return orderBumpsContext.priceMap;
    }

    const map = new Map<
      string,
      {
        id: string;
        currencyOptions: Record<string, { amount: number; date?: string; rate?: number; lock?: boolean }>;
        recurring: boolean;
        interval: string | null;
        intervalCount: number | null;
      }
    >();
    if (productsContext?.products) {
      productsContext.products.forEach((product) => {
        if (product.variants) {
          product.variants.forEach((variant) => {
            if (variant.prices) {
              variant.prices.forEach((price) => {
                if (price.currencyOptions) {
                  map.set(price.id, {
                    id: price.id,
                    currencyOptions: price.currencyOptions,
                    recurring: price.recurring ?? false,
                    interval: price.interval ?? null,
                    intervalCount: price.intervalCount ?? null,
                  });
                }
              });
            }
          });
        }
      });
    }
    return map;
  }, [orderBumpsContext?.priceMap, productsContext?.products]);

  // Extract order bumps from upsells and create a map of upsellId -> upsell info
  const { extractedOrderBumps, upsellMap } = useMemo(() => {
    // If order-bumps-provider is available, use its data
    if (orderBumpsContext) {
      const map = new Map<string, { id: string; offerTitle: string; enabled: boolean }>();
      // Extract upsell info from order bumps
      orderBumpsContext.orderBumps.forEach((ob) => {
        if (ob.upsellId && !map.has(ob.upsellId)) {
          const upsell = (ob as any).upsell;
          if (upsell) {
            map.set(ob.upsellId, {
              id: upsell.id,
              offerTitle: upsell.offerTitle,
              enabled: upsell.enabled,
            });
          }
        }
      });
      return { extractedOrderBumps: orderBumpsContext.orderBumps, upsellMap: map };
    }

    // Fallback: extract from upsells
    if (!upsellsContext?.upsells) return { extractedOrderBumps: [], upsellMap: new Map() };

    const map = new Map<string, { id: string; offerTitle: string; enabled: boolean }>();
    const bumps = upsellsContext.upsells
      .filter((upsell) => upsell.type === 'orderbump' && upsell.enabled && upsell.orderBumpOffers)
      .flatMap((upsell) => {
        map.set(upsell.id, {
          id: upsell.id,
          offerTitle: upsell.offerTitle,
          enabled: upsell.enabled ?? false,
        });
        return upsell.orderBumpOffers!;
      });

    return { extractedOrderBumps: bumps, upsellMap: map };
  }, [orderBumpsContext, upsellsContext?.upsells]);

  // Get order bumps from injected props or extracted from upsells/order-bumps-provider
  const contextOrderBumps = injectedOrderBumps !== undefined ? injectedOrderBumps : extractedOrderBumps;
  const contextLoading =
    injectedLoading !== undefined
      ? injectedLoading
      : orderBumpsContext?.loading || upsellsContext?.loading || false;

  // Filter order bumps by search query
  const filteredOrderBumps = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return contextOrderBumps;
    }

    const query = debouncedSearch.toLowerCase();
    return contextOrderBumps.filter((ob) => {
      const upsellInfo = upsellMap.get(ob.upsellId);
      const upsellTitle = upsellInfo?.offerTitle?.toLowerCase() || '';
      const type = ob.type?.toLowerCase() || '';
      const variant = ob.variantId ? variantMap.get(ob.variantId) : null;
      const variantName = variant?.name?.toLowerCase() || '';

      return upsellTitle.includes(query) || type.includes(query) || variantName.includes(query);
    });
  }, [contextOrderBumps, debouncedSearch, upsellMap, variantMap]);

  // Group order bumps by upsell
  const groupedByUpsell = useMemo(() => {
    const groups = new Map<string, OrderBumpOfferItem[]>();

    filteredOrderBumps.forEach((ob) => {
      const upsellId = ob.upsellId;
      if (!groups.has(upsellId)) {
        groups.set(upsellId, []);
      }
      groups.get(upsellId)!.push(ob);
    });

    return Array.from(groups.entries()).map(([upsellId, orderBumps]) => ({
      upsellId,
      upsell: upsellMap.get(upsellId),
      orderBumps,
    }));
  }, [filteredOrderBumps, upsellMap]);

  // Determine which upsell groups should be open
  useEffect(() => {
    // If searching, expand all if result count is small
    if (debouncedSearch && groupedByUpsell.length > 0 && groupedByUpsell.length <= 10) {
      setOpenUpsells(new Set(groupedByUpsell.map((g) => g.upsellId)));
      return;
    }

    // If something is selected, find and open the group containing the selected order bump
    if (value && groupedByUpsell.length > 0) {
      const selectedGroup = groupedByUpsell.find((group) => group.orderBumps.some((ob) => ob.id === value));
      if (selectedGroup) {
        setOpenUpsells(new Set([selectedGroup.upsellId]));
        return;
      }
    }

    // If nothing is selected, keep the first one open
    if (!value && groupedByUpsell.length > 0) {
      setOpenUpsells(new Set([groupedByUpsell[0].upsellId]));
    }
  }, [value, groupedByUpsell, debouncedSearch]);

  const toggleUpsell = (upsellId: string) => {
    setOpenUpsells((prev) => {
      const next = new Set(prev);
      if (next.has(upsellId)) {
        next.delete(upsellId);
      } else {
        next.add(upsellId);
      }
      return next;
    });
  };

  // Memoized price info getter
  const getPriceInfo = useMemo(
    () =>
      (
        orderBump: OrderBumpOfferItem,
      ): { amount: number; currency: string; recurring?: boolean; interval?: string | null } | null => {
        if (!orderBump.displayPrice || !orderBump.priceId) {
          return null;
        }

        const price = priceMap.get(orderBump.priceId);
        if (!price?.currencyOptions) {
          return null;
        }

        const currencies = Object.keys(price.currencyOptions);
        if (currencies.length > 0) {
          const currency = currencies[0];
          const currencyData = price.currencyOptions[currency];
          if (currencyData && typeof currencyData.amount === 'number') {
            return {
              amount: currencyData.amount,
              currency: currency.toUpperCase(),
              recurring: price.recurring,
              interval: price.interval,
            };
          }
        }
        return null;
      },
    [priceMap],
  );

  // Only show skeletons on INITIAL load (no data yet)
  // This prevents the "accordion effect" when refreshing with existing data
  if (contextLoading && filteredOrderBumps.length === 0) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="mb-2 h-9 rounded bg-gray-200" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-2">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-1">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-64 rounded bg-gray-200" />
              </div>
              <div className="h-4 w-4 rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Search Input */}
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={t('configEditor.formElements.searchOrderBumps')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-9 pl-9 pr-9"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="text-muted-foreground hover:text-foreground absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results count */}
      {debouncedSearch && filteredOrderBumps.length > 0 && (
        <div className="text-muted-foreground text-xs">
          Found {filteredOrderBumps.length} {filteredOrderBumps.length === 1 ? 'order bump' : 'order bumps'}
        </div>
      )}

      {/* Upsell Groups */}
      <RadioGroup name={`order-bump-selector-${instanceId}`} value={value} onValueChange={onChange}>
        <div className="h-[400px] space-y-1 overflow-y-auto p-1.5">
          {!filteredOrderBumps || filteredOrderBumps.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8">
              <Package className="text-muted-foreground mb-2 h-8 w-8" />
              <p className="text-muted-foreground text-sm">
                {debouncedSearch
                  ? `No order bumps found matching "${debouncedSearch}"`
                  : 'No order bumps available'}
              </p>
            </div>
          ) : (
            groupedByUpsell.map((group) => {
              const isOpen = openUpsells.has(group.upsellId);
              const orderBumpCount = group.orderBumps.length;

              // Get first order bump image (from overrideImageUrl or variant)
              const firstOrderBumpImage =
                group.orderBumps.find((ob) => ob.overrideImageUrl)?.overrideImageUrl ||
                group.orderBumps
                  .map((ob) => (ob.variantId ? variantMap.get(ob.variantId)?.imageUrl : null))
                  .find((url) => url) ||
                null;

              return (
                <Collapsible
                  key={group.upsellId}
                  open={isOpen}
                  onOpenChange={() => toggleUpsell(group.upsellId)}
                >
                  <div className="rounded-md border">
                    <CollapsibleTrigger className="hover:bg-accent/50 flex w-full items-center justify-between gap-2 p-2 transition-colors">
                      {firstOrderBumpImage ? (
                        <img
                          src={firstOrderBumpImage}
                          alt={group.upsell?.offerTitle || 'Order Bump'}
                          className="h-10 w-10 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded">
                          <ImageOff className="text-muted-foreground h-5 w-5" />
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-semibold leading-tight">
                            {group.upsell?.offerTitle || 'Order Bump Group'}
                          </h2>
                          <span className="text-muted-foreground text-xs">
                            ({orderBumpCount} {orderBumpCount === 1 ? 'offer' : 'offers'})
                          </span>
                        </div>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform duration-200',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-2 p-2 pt-0">
                        {group.orderBumps.length > 0 ? (
                          group.orderBumps.map((orderBump) => (
                            <OrderBumpCard
                              key={orderBump.id}
                              orderBump={orderBump}
                              isSelected={value === orderBump.id}
                              onSelect={() => onChange(orderBump.id)}
                              getPriceInfo={getPriceInfo}
                              variantMap={variantMap}
                            />
                          ))
                        ) : (
                          <p className="text-muted-foreground text-xs">No order bumps available</p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })
          )}
        </div>
      </RadioGroup>
    </div>
  );
}

export default OrderBumpSelector;
